// src/components/SyncProgressDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  LinearProgress,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon, CloudDownload } from '@mui/icons-material';
import AutoCacheService from '../services/AutoCacheService';

const SyncProgressDialog = ({ open, onClose, autoClose = true }) => {
  const [progress, setProgress] = useState({ total: 0, completed: 0, sections: {} });
  const [error, setError] = useState(null);
  
  // Effect to update progress
  useEffect(() => {
    if (!open) return;
    
    const updateProgress = () => {
      const currentProgress = AutoCacheService.getProgress();
      setProgress(currentProgress);
      
      if (currentProgress.isComplete) {
        if (autoClose) {
          // Auto close after 2 seconds when complete
          setTimeout(() => {
            if (onClose) onClose();
          }, 2000);
        }
        return;
      }
      
      // Update again in 500ms
      setTimeout(updateProgress, 500);
    };
    
    updateProgress();
  }, [open, autoClose, onClose]);
  
  // Calculate overall progress percentage
  const getOverallProgress = () => {
    if (progress.total === 0) return 0;
    return Math.round((progress.completed / progress.total) * 100);
  };
  
  // Calculate section progress percentage
  const getSectionProgress = (section) => {
    if (!section || section.total === 0) return 0;
    return Math.round((section.completed / section.total) * 100);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        // Only allow closing if sync is complete or user explicitly closes
        if (progress.isComplete || reason !== 'backdropClick') {
          if (onClose) onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Syncing Documents for Offline Use
      </DialogTitle>
      
      <DialogContent>
        {/* Overall progress */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body1" fontWeight="medium">
              Overall Progress: {getOverallProgress()}%
            </Typography>
            <Box sx={{ ml: 'auto' }}>
              {progress.isComplete ? (
                <CheckCircle color="success" />
              ) : (
                <CircularProgress size={20} />
              )}
            </Box>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={getOverallProgress()} 
            sx={{ height: 10, borderRadius: 5 }}
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {progress.completed} of {progress.total} sections completed
          </Typography>
        </Box>
        
        {/* Sections progress */}
        <Typography variant="subtitle1" gutterBottom>
          Sections
        </Typography>
        
        <List sx={{ bgcolor: 'background.paper', borderRadius: 1, mb: 2 }}>
          {Object.entries(progress.sections).map(([path, section], index) => (
            <React.Fragment key={path}>
              <ListItem>
                <ListItemText 
                  primary={section.name} 
                  secondary={`${section.completed} of ${section.total} documents`} 
                />
                <Box sx={{ minWidth: 50, textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    {getSectionProgress(section)}%
                  </Typography>
                </Box>
              </ListItem>
              {index < Object.entries(progress.sections).length - 1 && <Divider />}
            </React.Fragment>
          ))}
          
          {Object.keys(progress.sections).length === 0 && (
            <ListItem>
              <ListItemText 
                primary="Preparing to sync..." 
                secondary="Identifying documents to download"
              />
              <CircularProgress size={20} />
            </ListItem>
          )}
        </List>
        
        {/* Error message if any */}
        {error && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography color="error" variant="body2">
              <ErrorIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              {error}
            </Typography>
          </Box>
        )}
        
        {/* Completed message */}
        {progress.isComplete && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography color="success.dark" variant="body1">
              <CheckCircle fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              All documents have been downloaded for offline use!
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={onClose}
          color="primary"
          disabled={!progress.isComplete && AutoCacheService.isSyncing}
        >
          {progress.isComplete ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SyncProgressDialog;